import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import FormRenderer from "../../components/FeedbackFormRenderer";
import FeedbackSuccess from "../../components/FeedbackSuccess";
import {
  useGetFormByResponseQuery,
  useUpdateFormResponseMutation,
} from "@/redux/reducer/feedback.api";
import { FORM_CONSTANTS } from "../../constants/form.constants";
import { getQuestionText } from "@/utils/formUtils";
import FormWrapper from "@/components/FormWrapper/FormWrapper";
import AlreadyResponded from "@/components/AlreadyResponded";
import { useTranslation } from "react-i18next";

/**
 * CandidateFeedbackForm
 * Main page component for candidate feedback form
 * Handles form loading, submission, and success state
 */
const CandidateFeedbackForm: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Extract query parameters
  const responseId = searchParams.get("id") || "";
  const tenantId = searchParams.get("tid") || "";
  const language = searchParams.get("lang") || "";

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [updateFormResponse] = useUpdateFormResponseMutation();

  // Fetch form data
  const {
    data: formData,
    isLoading,
    error,
  } = useGetFormByResponseQuery(
    { responseId, tenantId, language },
    { skip: !responseId || !tenantId }
  );

  // Handle redirects for invalid states
  useEffect(() => {
    if (!responseId || !tenantId) {
      navigate("/");
    } else if (error || (!isLoading && !formData)) {
      navigate("/error/Error404");
    }
  }, [responseId, tenantId, error, isLoading, formData, navigate]);

  const handleSubmit = async (responses: Record<string, any>) => {
    try {
      const answers = formData?.formJson?.questions.map((q) => {
        const questionText = getQuestionText(
          q,
          Number(formData?.languageId) || FORM_CONSTANTS.DEFAULT_LANGUAGE_ID
        );

        return {
          questionId: q.id,
          questionText,
          type: q.typeId,
          answer: q.scale ? Number(responses[q.id]) : responses[q.id],
          scale: q.scale,
        };
      });

      const payload = {
        id: responseId,
        formId: formData?.formJson?.id || "",
        tenantId,
        rawJson: JSON.stringify(answers),
        feedbackLanguage: language,
      };

      await updateFormResponse(payload)
        .unwrap()
        .finally(() => {
          setIsSubmitted(true);
        });
    } catch (err) {
      console.error("Failed to submit feedback:", err);
      alert(t("Form.Errors.SubmissionFailed"));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t("Form.Loading")}</span>
        </div>
      </div>
    );
  }

  // Redirecting state
  if (!responseId || !tenantId || error || !formData) {
    return null;
  }

  return (
    <FormWrapper
      metaData={formData?.metaData}
      showTitle={true}
    >
      {isSubmitted && <FeedbackSuccess />}
      {!isSubmitted &&
        (formData.isSubmitted ? (
          <AlreadyResponded />
        ) : (
          <FormRenderer
            formJson={formData.formJson}
            onSubmit={handleSubmit}
          />
        ))}
    </FormWrapper>
  );
};

export default CandidateFeedbackForm;
